import { Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { env } from '../../../environments/environment';
import { FlagGroupService } from '../../flag/flag.group.service';
import { FlagService } from '../../flag/flag.service';
import { FlagGroup, FlagGroupTag } from '../../shared/interfaces';
import { FlagGroupTemplateComponent } from '../flag-group-template/flag-group-template.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-edit-group',
  templateUrl: './edit.group.component.html',
  styleUrls: ['./edit.group.component.scss'],
})
export class EditGroupComponent implements OnChanges {
  @ViewChild('flagGroupTemplateModal', { static: true }) public flagGroupTemplateModal: FlagGroupTemplateComponent;
  @Output() messageEvent = new EventEmitter<boolean>();
  @Output() editGroup = new EventEmitter<any>();
  @Input() allGroups = [];
  @Input() flagGroupTags: FlagGroupTag[] = [];
  get flagSubtypes() {
    const flagType = this.form.get('type').value;
    if (flagType) {
      return flagType.subtypes;
    } else {
      return [];
    }
  }

  constructor(
    private flagGroupService: FlagGroupService,
    private fb: UntypedFormBuilder,
    private flagService: FlagService,
    private toast: ToastrService,
  ) {
    this.form = this.fb.group({
      id: '',
      name: ['', [Validators.required, this.groupNameValidator()]],
      inspectionId: '',
      type: '',
      subtype: '',
      description: '',
      helpPictures: this.fb.array([]),

      violation: this.fb.group({
        elevatedRisk: '',
        deadline: '',
        whyDoesThisMatter: '',
        actionRequested: '',
        internalNotes: '',
        documentationRequested: '',
        urlTitle: '',
        url: '',
      }),

      discrepancy: this.fb.group({
        stated: '',
        actual: '',
        resultOfTheFinding: '',
        impact: '',
        effectiveDate: '',
        commentsForReport: '',
        urlTitle: '',
        url: '',
      }),
    });

    this.form.get('type').valueChanges.subscribe(type => {
      if (type) {
        this.form.get('subtype').setValue(type ? type.subtypes[0] : null);
      }
    });
  }

  form: UntypedFormGroup;
  @Output() groupSaved = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Input() group: FlagGroup;
  @Input() readOnly = false;

  @ViewChild('helpPicturesModal', { static: true }) public helpPicturesModal: ModalDirective;
  fileToUpload: File;

  flagTypes;
  hideLegacy;
  saving = false;

  originalHelpPictures;

  async ngOnChanges(changes) {
    if (!this.flagTypes) {
      this.flagTypes = await this.flagService.listTypes();

      const subtypeInfo = await this.flagService.subtypeInfo;
      this.flagTypes.forEach(type => {
        type.subtypes.forEach(subtype => {
          subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, subtype.name).name;
        });
      });
    }

    if ('readOnly' in changes) {
      if (this.readOnly) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }

    if ('group' in changes) {
      if (this.group) {
        this.group.helpPictures = this.group.helpPictures || [];
      }
      this.form.patchValue(this.group);

      this.form
        .get('violation.deadline')
        .patchValue(this.group.violation.deadline ? new Date(this.group.violation.deadline) : null);

      this.form
        .get('discrepancy.effectiveDate')
        .patchValue(this.group.discrepancy.effectiveDate ? new Date(this.group.discrepancy.effectiveDate) : null);

      if (this.group && this.group.subtype) {
        this.form.get('type').setValue(this.flagTypes.find(ft => ft.id === this.group.subtype.typeId));
        this.form.get('subtype').setValue(this.flagSubtypes.find(fst => fst.id === this.group.subtype.id));
        this.hideLegacy = this.group.subtype.name !== 'Legacy';
      }

      const wasDisabled = this.form.disabled;
      const helpPictures = this.form.get('helpPictures') as UntypedFormArray;
      while (helpPictures.length > 0) {
        helpPictures.removeAt(0);
      }

      if (this.group.helpPictures && this.group.helpPictures.length) {
        this.group.helpPictures.forEach(hp => {
          const group = this.fb.group({
            caption: wasDisabled ? [{ value: '', disabled: true }] : '',
            title: wasDisabled ? [{ value: '', disabled: true }] : '',
            ...Object.entries(hp).reduce((acc, [key, value]) => {
              acc[key] = wasDisabled ? [{ value, disabled: true }] : value;
              return acc;
            }, {} as any),
          });

          helpPictures.push(group);
        });
      }
    }

    this.form.get('violation.deadline').disable();
  }

  groupNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const groupName = control.value || '';
      const groupExistWithOtherSubtype = this.allGroups.find(
        g => g.name.toUpperCase() === groupName.toUpperCase() && g.name.toUpperCase() !== this.group.name.toUpperCase(),
      );
      if (groupExistWithOtherSubtype) {
        return { groupNameExists: true };
      }
      return null;
    };
  }

  async save() {
    if (this.saving) return;
    if (this.form.valid) {
      this.saving = true;
      const formData = this.form.value;
      const group = await this.flagGroupService.edit(this.group.id, formData);
      this.saving = false;
      this.groupSaved.emit(group);
    }
  }

  getValidationClass(field: string) {
    const control = this.form.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  setEffectiveDate(to) {
    switch (to) {
      case '+30d':
        const d30 = new Date();
        d30.setDate(d30.getDate() + 30);
        this.form.get('discrepancy.effectiveDate').setValue(d30);
        break;

      case '+60d':
        const d60 = new Date();
        d60.setDate(d60.getDate() + 60);
        this.form.get('discrepancy.effectiveDate').setValue(d60);
        break;

      case '+90d':
        const d90 = new Date();
        d90.setDate(d90.getDate() + 90);
        this.form.get('discrepancy.effectiveDate').setValue(d90);
        break;
    }
  }

  setDeadline(to) {
    switch (to) {
      case '+30d':
        const d30 = new Date();
        d30.setDate(d30.getDate() + 30);
        this.form.get('violation.deadline').setValue(d30);
        break;

      case '+60d':
        const d60 = new Date();
        d60.setDate(d60.getDate() + 60);
        this.form.get('violation.deadline').setValue(d60);
        break;

      case '+90d':
        const d90 = new Date();
        d90.setDate(d90.getDate() + 90);
        this.form.get('violation.deadline').setValue(d90);
        break;
    }
  }

  openDocPictures() {
    this.originalHelpPictures = (this.form.get('helpPictures') as UntypedFormArray).getRawValue();
    this.helpPicturesModal.show();
  }

  closeHelpPictures() {
    this.helpPicturesModal.hide();
  }

  cancelHelpPictures() {
    this.helpPicturesModal.hide();
    this.form.get('helpPictures').setValue(this.originalHelpPictures);
  }

  setUploadFile(files: FileList) {
    this.fileToUpload = files.item(0);
  }

  removeHelpPicture(i) {
    (this.form.get('helpPictures') as UntypedFormArray).removeAt(i);
  }

  getSrc(img) {
    return `${env.APIUrl}/image/download/${img.key}`;
  }

  async uploadHelpPicture(input: HTMLInputElement) {
    const formData = new FormData();
    formData.append('upload', this.fileToUpload, this.fileToUpload.name);
    const uploadInfo = await this.flagService.uploadPicture(formData);
    input.value = null;
    (this.form.get('helpPictures') as UntypedFormArray).push(
      this.fb.group({
        ...uploadInfo,
        caption: '',
        title: '',
      }),
    );
  }

  applyTemplate() {
    const flagType = this.form.get('type')?.value;

    if (!this.group.subtype) {
      const errorMessage =
        flagType && typeof flagType === 'object' ? 'Please save the Flag Type first' : 'Please select the Flag';

      this.toast.error(errorMessage);
      return;
    }

    this.flagGroupTemplateModal.show();
  }

  /* https://github.com/ng-select/ng-select/issues/809 */
  addTag = x => x;

  updateData(check: boolean) {
    if (check) {
      this.messageEvent.emit(true);
    }
  }

  editGroupByTemplate(group) {
    this.editGroup.emit(group);
  }
}
